import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Card from '../../components/Card';
import Button from '../../components/Button';
import DataTable, { Column } from '../../components/DataTable';
import ConfirmDialog from '../../components/ConfirmDialog';
import { 
  Users, 
  Plus, 
  UserCheck,
  UserX,
  Mail,
  Phone,
  Building,
  Shield,
  Wrench as WrenchIcon,
  Download as DownloadIcon,
  Upload as UploadIcon
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useCrudApi } from '../../hooks/useApi';
import apiService from '../../services/apiService';

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: 'admin' | 'technician' | 'client';
  company?: string;
  isActive: boolean;
  lastLogin?: string;
  avatar?: string;
  departements?: string[];
  address?: {
    street?: string;
    city?: string;
    postalCode?: string;
    country?: string;
  };
}

interface UserFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: 'admin' | 'technician' | 'client';
  company?: string;
  password?: string;
  confirmPassword?: string;
  departements?: string[];
  availability?: {
    status: 'available' | 'unavailable';
    nextDayOff?: string;
  };
  isActive: boolean;
  address?: {
    street?: string;
    city?: string;
    postalCode?: string;
    country?: string;
  };
}

const UserManagement: React.FC = () => {
  const navigate = useNavigate();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [roleFilter, setRoleFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const userApiConfig = useMemo(() => ({
    getAll: apiService.users.getAll,
    getById: apiService.users.getById,
    create: apiService.users.create,
    update: apiService.users.update,
    delete: apiService.users.delete
  }), []);

  const crudApiOptions = useMemo(() => ({
    successMessages: {
      create: 'Utilisateur créé avec succès',
      update: 'Utilisateur mis à jour avec succès',
      delete: 'Utilisateur supprimé avec succès'
    }
  }), []);

  const { 
    items: users, 
    total,
    isLoading,
    fetchItems: fetchUsers,
    createItem: createUser,
    updateItem: updateUser,
    deleteItem: deleteUser,
  } = useCrudApi<User, UserFormData, Partial<UserFormData>>(
    userApiConfig,
    crudApiOptions
  );

  const { register, handleSubmit, reset, formState: { errors } } = useForm<UserFormData>();
  const location = useLocation();

  const loadUsers = useCallback(async () => {
    const params: any = {
      page: currentPage,
      limit: pageSize
    };

    if (roleFilter !== 'all') {
      params.role = roleFilter;
    }

    if (searchTerm) {
      params.search = searchTerm;
    }

    await fetchUsers(params);
  }, [currentPage, pageSize, roleFilter, searchTerm, fetchUsers]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (location.state?.userId && location.state?.openEditModal) {
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleCreateUser = async (data: UserFormData) => {
    if (data.password !== data.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    try {
      await createUser(data);
      setIsCreateModalOpen(false);
      reset();
      loadUsers();
    } catch (error: any) {
      // error is already handled in useCrudApi
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUserId) return;

    try {
      await deleteUser(selectedUserId);
      setIsDeleteDialogOpen(false);
      loadUsers();
    } catch (error) {
      // error is already handled in useCrudApi
    }
  };

  const handleToggleStatus = async (userId: string) => {
    try {
      await apiService.users.toggleStatus(userId);
      loadUsers();
    } catch (error) {
      toast.error('Erreur lors du changement de statut');
    }
  };

  const openDeleteDialog = (userId: string) => {
    setSelectedUserId(userId);
    setIsDeleteDialogOpen(true);
  };

  const handleExportUsers = async () => {
    try {
      toast.loading('Exportation des utilisateurs...');
      const response = await apiService.users.exportCsv();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'users.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.dismiss();
      toast.success('Utilisateurs exportés avec succès !');
    } catch (error) {
      toast.dismiss();
      toast.error("Erreur lors de l'exportation des utilisateurs.");
    }
  };

  const handleImportUsers = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) {
      toast.error('Veuillez sélectionner un fichier CSV.');
      return;
    }

    const file = event.target.files[0];
    const formData = new FormData();
    formData.append('usersCsv', file);

    try {
      toast.loading('Importation des utilisateurs...');
      const response = await apiService.users.importCsv(formData);
      toast.dismiss();
      if (response.data.success) {
        toast.success(`Importation terminée : ${response.data.data.imported} créés, ${response.data.data.updated} mis à jour.`);
        if (response.data.data.errors.length > 0) {
          toast.error(`Certaines lignes ont échoué : ${response.data.data.errors.length} erreurs.`);
        }
        setIsImportModalOpen(false);
        loadUsers();
      } else {
        toast.error(`Erreur d'importation : ${response.data.message}`);
      }
    } catch (error: any) {
      toast.dismiss();
      toast.error(`Erreur lors de l'importation : ${error.response?.data?.message || error.message}`);
    }
  };

  const columns: Column<User>[] = [
    {
      header: 'Utilisateur',
      accessor: (user: User) => (
        <div className="flex items-center">
          <img
            src={user.avatar || `https://ui-avatars.com/api/?name=${user.firstName}+${user.lastName}&background=random`}
            alt={`${user.firstName} ${user.lastName}`}
            className="w-10 h-10 rounded-full object-cover mr-4 cursor-pointer"
            onClick={() => {}}
          />
          <div>
            <div className="font-medium text-white-900">{user.firstName} {user.lastName}</div>
            <div className="text-sm text-white-500">{user.company || ''}</div>
          </div>
        </div>
      ),
      sortable: false
    },
    {
      header: 'Contact',
      accessor: (user: User) => (
        <div className="space-y-1">
          <div className="flex items-center text-sm">
            <Mail className="w-4 h-4 mr-2 text-white-400" />
            {user.email}
          </div>
          {user.phone && (
            <div className="flex items-center text-sm text-white-500">
              <Phone className="w-4 h-4 mr-2 text-white-400" />
              {user.phone}
            </div>
          )}
        </div>
      ),
      sortable: false
    },
    {
      header: 'Rôle',
      accessor: 'role',
      cell: (user: User) => {
        const roleConfig = {
          admin: { color: 'bg-purple-100 text-purple-800', label: 'Administrateur' },
          technician: { color: 'bg-blue-100 text-blue-800', label: 'Technicien' },
          client: { color: 'bg-green-100 text-green-800', label: 'Client' },
        };
        
        const config = roleConfig[user.role];
        
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
            {config.label}
          </span>
        );
      },
      sortable: true
    },
    {
      header: 'Statut',
      accessor: 'isActive',
      cell: (user: User) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${ 
          user.isActive ? 'bg-success-100 text-success-800' : 'bg-error-100 text-error-800'
        }`}>
          {user.isActive ? (
            <>
              <UserCheck className="w-3 h-3 mr-1" />
              Actif
            </>
          ) : (
            <>
              <UserX className="w-3 h-3 mr-1" />
              Inactif
            </>
          )}
        </span>
      ),
      sortable: true
    },
    {
      header: 'Dernière connexion',
      accessor: 'lastLogin',
      cell: (user: User) => (
        <span className="text-sm text-white-500">
          {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString('fr-FR') : 'Jamais'}
        </span>
      ),
      sortable: true
    },
    {
      header: 'Départements',
      accessor: (user: User) => (
        <div className="flex flex-wrap gap-1">
          {(user.departements || []).map((d) => (
            <span
              key={d}
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-800"
            >
              {d}
            </span>
          ))}
          {(!user.departements || user.departements.length === 0) && (
            <span className="text-xs text-gray-400">—</span>
          )}
        </div>
      ),
      sortable: false
    },
  ];

  const stats = {
    totalUsers: total,
    activeUsers: (users || []).filter(u => u.isActive).length,
    adminCount: (users || []).filter(u => u.role === 'admin').length,
    techCount: (users || []).filter(u => u.role === 'technician').length,
    clientCount: (users || []).filter(u => u.role === 'client').length
  };

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-white-900 flex items-center">
              <Users className="w-8 h-8 mr-3 text-primary-600" />
              Gestion des Utilisateurs
            </h1>
            <p className="text-white-600 mt-2">
              Gérez les comptes utilisateurs, techniciens et clients
            </p>
          </div>
          <div className="flex space-x-4">
            <Button 
              size="lg" 
              onClick={handleExportUsers}
              className="flex items-center bg-blue-600 hover:bg-blue-700"
            >
              <DownloadIcon className="w-5 h-5 mr-2" />
              Exporter CSV
            </Button>
            <Button 
              size="lg" 
              onClick={() => setIsImportModalOpen(true)}
              className="flex items-center bg-green-600 hover:bg-green-700"
            >
              <UploadIcon className="w-5 h-5 mr-2" />
              Importer CSV
            </Button>
            <Button 
              size="lg" 
              onClick={() => navigate('/admin/users/add')}
              className="flex items-center"
            >
              <Plus className="w-5 h-5 mr-2" />
              Nouvel Utilisateur
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-white-600">Total Utilisateurs</p>
                <p className="text-2xl font-bold text-white-900">{stats.totalUsers}</p>
              </div>
              <Users className="w-8 h-8 text-primary-600" />
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-white-600">Actifs</p>
                <p className="text-2xl font-bold text-success-600">{stats.activeUsers}</p>
              </div>
              <UserCheck className="w-8 h-8 text-success-600" />
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-white-600">Administrateurs</p>
                <p className="text-2xl font-bold text-purple-600">{stats.adminCount}</p>
              </div>
              <Shield className="w-8 h-8 text-purple-600" />
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-white-600">Techniciens</p>
                <p className="text-2xl font-bold text-blue-600">{stats.techCount}</p>
              </div>
              <WrenchIcon className="w-8 h-8 text-blue-600" />
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-white-600">Clients</p>
                <p className="text-2xl font-bold text-green-600">{stats.clientCount}</p>
              </div>
              <Building className="w-8 h-8 text-green-600" />
            </div>
          </Card>
        </div>

        {/* Users Table */}
        <DataTable
          data={users}
          columns={columns}
          keyField="_id"
          title="Liste des utilisateurs"
          isLoading={isLoading}
          pagination={{
            page: currentPage,
            limit: pageSize,
            total,
            onPageChange: setCurrentPage,
            onLimitChange: setPageSize
          }}
          actions={{
            view: (user) => {
              navigate(`/admin/users/view/${user._id}`);
            },
            edit: (user) => navigate(`/admin/users/edit/${user._id}`),
            delete: (user) => openDeleteDialog(user._id),
            custom: [
              {
                label: 'Statut',
                icon: <UserCheck className="w-4 h-4" />,
                onClick: (_e, user) => handleToggleStatus(user._id),
                color: 'text-blue-600'
              }
            ]
          }}
          filters={
            <select
              id="roleFilter"
              name="roleFilter"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="border border-white-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">Tous les rône</option>
              <option value="admin">Administrateurs</option>
              <option value="tech">Techniciens</option>
              <option value="client">Clients</option>
            </select>
          }
          searchPlaceholder="Rechercher par nom, email ou entreprise..."
          onSearch={setSearchTerm}
          emptyMessage="Aucun utilisateur trouvé"
        />

        {/* Import Modal */}
        {isImportModalOpen && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
            <div className="bg-white p-8 rounded-lg shadow-xl w-96">
              <h3 className="text-xl font-bold mb-4 text-gray-800">Importer des Utilisateurs (CSV)</h3>
              <p className="text-gray-600 mb-4">Veuillez sélectionner un fichier CSV contenant les données des utilisateurs.</p>
              <input 
                type="file" 
                accept=".csv" 
                onChange={handleImportUsers}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-full file:border-0
                  file:text-sm file:font-semibold
                  file:bg-primary-50 file:text-primary-700
                  hover:file:bg-primary-100
                "
              />
              <div className="mt-6 flex justify-end space-x-3">
                <Button 
                  variant="secondary" 
                  onClick={() => setIsImportModalOpen(false)}
                >
                  Annuler
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteUser}
          title="Supprimer l\'utilisateur"
          message="Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action est irréversible."
          confirmText="Supprimer"
          cancelText="Annuler"
          type="danger"
        />
      </div>
    </Layout>
  );
};

export default UserManagement;